import type { ReactNode } from "react";
import { ButtonRing } from "@/components/brand/ButtonRing";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Panel } from "@/components/ui/Panel";
import { useTranslation } from "react-i18next";
import { ApiClientError } from "@/lib/api";

export function LoadingState({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-ink-faint">
      <ButtonRing size={32} tone="brand" holes={4} progress={0.7} spinning />
      <span className="text-sm">{label ?? t("common.loading")}</span>
    </div>
  );
}

/** Loading chrome for a list-page edit overlay — never in document flow. */
export function ModalLoading({
  title,
  closeLabel,
  onClose,
  label,
}: {
  title: ReactNode;
  closeLabel: string;
  onClose: () => void;
  label?: string;
}) {
  return (
    <Modal title={title} closeLabel={closeLabel} onClose={onClose}>
      <LoadingState label={label} />
    </Modal>
  );
}

/** Error chrome for a list-page edit overlay — never in document flow. */
export function ModalError({
  title,
  closeLabel,
  onClose,
  message,
  onRetry,
}: {
  title: ReactNode;
  closeLabel: string;
  onClose: () => void;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <Modal title={title} closeLabel={closeLabel} onClose={onClose}>
      <div className="p-5">
        <ErrorState message={message} onRetry={onRetry} />
      </div>
    </Modal>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Panel className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <ButtonRing size={36} tone="danger" holes={4} progress={0.25} />
      <p className="max-w-sm text-sm text-ink-soft">{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      )}
    </Panel>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <Panel className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <ButtonRing size={34} tone="neutral" holes={4} progress={0} />
      <p className="text-sm font-medium text-ink">{title}</p>
      {hint && <p className="max-w-sm text-xs text-ink-faint">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </Panel>
  );
}

/**
 * Lỗi tải dữ liệu, PHÂN BIỆT 403 với lỗi thật.
 *
 * "Không tải được dữ liệu — Thử lại" khi máy chủ trả 403 là nói sai hai lần:
 * dữ liệu tải được bình thường, chỉ là người này không được xem; và nút Thử
 * lại sẽ gọi lại đúng route đó để nhận đúng 403 đó, mãi mãi. Người dùng bấm
 * năm lần rồi báo là hệ thống hỏng.
 */
export function QueryErrorState({
  error,
  message,
  onRetry,
  forbiddenMessage,
}: {
  error: unknown;
  message: string;
  onRetry?: () => void;
  forbiddenMessage: string;
}) {
  const forbidden = error instanceof ApiClientError && error.status === 403;
  return forbidden ? (
    <ErrorState message={forbiddenMessage} />
  ) : (
    <ErrorState message={message} onRetry={onRetry} />
  );
}
